import { create } from "zustand";

/**
 * Dock store (2026-07-15) — one panel system, many kinds.
 *
 * The dock is a GRID: columns side by side, each a stack of cards, each card
 * holding tabs. Terminal, browser, files and the chat artifact canvas are all
 * just `kind`s plugging into it. The owner's call was explicit — "one dock, many
 * kinds" — because a second parallel panel system beside the artifact canvas
 * would have meant two tab strips, two resizers, two close buttons, and
 * inevitable drift.
 *
 * Shape:
 *   DockPanel  = one TAB    (a document / a shell / a page)
 *   DockSlot   = one CARD   (tab strip + body; rounded, gap-separated)
 *   DockStack  = one COLUMN (cards stacked top-to-bottom)
 *   stacks[]   = the GRID, columns left-to-right
 *
 * `DockStack`, deliberately NOT `DockColumn`: that name belongs to a component.
 * A type and a component sharing a name reads fine in a diff and confuses every
 * reader afterwards.
 *
 * The contracts below are ported deliberately from the artifact canvas rather
 * than re-derived — each one was paid for:
 *   - add-or-focus by id  (auto-open + a manual click must not double a tab)
 *   - close -> neighbour  (never strand focus on nothing)
 *   - MAX_TABS eviction   (panels open WITHOUT being asked; unbounded = unusable)
 *   - close() HIDES       (it may only discard while nothing can re-show it —
 *                          the icon rail is a re-show, so the set is retained)
 */

export type PanelKind = "artifact" | "terminal" | "browser" | "files" | "inspector";

export interface DockPanel {
	/** Stable id. Re-opening the same thing focuses its tab instead of cloning. */
	id: string;
	kind: PanelKind;
	/** Tab label. Must be distinguishable — a strip reading "Terminal, Terminal,
	 *  Terminal" cannot be navigated (see artifactTitle's content-derived names). */
	title: string;
	/** Kind-specific payload. The dock host never reads this; the kind's
	 *  component does. Keeping it opaque is what lets a new kind ship without
	 *  touching the host — if the host ever needs to read it, the seam is wrong. */
	data?: unknown;
	/** Which MODULE this panel belongs to ("chat", "media", …). The dock is one
	 *  app-wide surface, but modules only show their own results (owner rule,
	 *  2026-08-12): panels are filtered to the current module at render.
	 *  Undefined = global — tools like the terminal follow you everywhere. */
	scope?: string;
}

export interface DockSlot {
	id: string;
	panels: DockPanel[];
	activeId: string | null;
	/** Vertical share of its column, as a flex-grow weight (not pixels).
	 *  Weights survive a column that changes height — pixel heights would have to
	 *  be re-clamped on every window resize, and would drift out of sync with the
	 *  container's real height. The resizer converts measured px back to weights. */
	size: number;
}

/** One COLUMN of cards. */
export interface DockStack {
	id: string;
	slots: DockSlot[];
	/** Horizontal share of the dock, as a flex-grow weight — same reasoning as
	 *  DockSlot.size, one axis over. */
	size: number;
}

const WIDTH_KEY = "agnt-dock-width";
const MIN_WIDTH = 340;

/** Every card / column starts with an equal share. */
const DEFAULT_SIZE = 1;

/** Per-slot tab ceiling. Same reasoning as the artifact canvas: panels can open
 *  without the user asking (auto-open), so an unbounded strip degrades to
 *  unnavigable. Eviction is safe — every kind can be re-opened from its source. */
export const MAX_TABS_PER_SLOT = 8;

function initialWidth(): number {
	const raw = typeof localStorage !== "undefined" ? localStorage.getItem(WIDTH_KEY) : null;
	const n = raw ? Number(raw) : NaN;
	return Number.isFinite(n) && n >= MIN_WIDTH ? n : 520;
}

export interface DockState {
	open: boolean;
	/** Columns, left to right. */
	stacks: DockStack[];
	width: number;
	/** The one card filling the dock, or null for the normal grid. A view mode,
	 *  not a layout fact — it is dropped the moment it would hide something the
	 *  user just asked for (see `keepMaximize`). */
	maximizedSlotId: string | null;
	/** Add a panel and focus it — or just focus it if already open (idempotent
	 *  by id). Lands in `slotId` when given, else the first card, else a new one. */
	openPanel: (panel: DockPanel, slotId?: string) => void;
	/** Close one tab. Emptying a card removes it; emptying a column removes the
	 *  column; emptying the last one closes the dock. */
	closePanel: (id: string) => void;
	/** Close a whole card and every tab in it — the card's own ✕. */
	closeSlot: (slotId: string) => void;
	/** Swap a panel for another IN PLACE, keeping its tab position and focus.
	 *  For "same thing, different version": an artifact's id is a content hash,
	 *  so flipping to another draft would otherwise `openPanel` a second tab and
	 *  litter the strip with drafts the user is stepping through, not collecting.
	 *  Inert if `next.id` already occupies another tab — that would duplicate. */
	replacePanel: (id: string, next: DockPanel) => void;
	setActive: (panelId: string) => void;
	/** Move a panel into its own new card below — the "split" half of movable. */
	splitPanelToNewSlot: (panelId: string) => void;
	/** Move a whole card — the "grab and move" half, driven by the card's
	 *  top-centre handle. `toStackIndex === stacks.length` means "make a new
	 *  column", which is how a second column comes into existence at all. */
	moveSlot: (slotId: string, toStackIndex: number, toIndex: number) => void;
	/** Fill the dock with one card / restore the grid. */
	toggleMaximize: (slotId: string) => void;
	/** Re-weight two adjacent cards within a column. The caller does the pixel
	 *  math (it owns the DOM measurements); the store owns the state + the check. */
	resizeSlotPair: (aId: string, bId: string, aSize: number, bSize: number) => void;
	/** Re-weight two adjacent columns. Same contract, one axis over. */
	resizeStackPair: (aId: string, bId: string, aSize: number, bSize: number) => void;
	/** Show the dock on demand (the icon-rail path), even with nothing in it. */
	openDock: () => void;
	/** Hide the dock, KEEPING its panels for the rail to bring back. */
	close: () => void;
	setWidth: (px: number) => void;
}

let _slotSeq = 0;
let _stackSeq = 0;
/** Deterministic ids — Math.random() would break test determinism and any future
 *  persistence of the layout. */
function nextSlotId(): string {
	_slotSeq += 1;
	return `slot-${_slotSeq}`;
}
function nextStackId(): string {
	_stackSeq += 1;
	return `stack-${_stackSeq}`;
}

/** Test seam: reset the counters so ids are predictable per test. */
export function __resetSlotSeq(): void {
	_slotSeq = 0;
	_stackSeq = 0;
}

function newSlot(panels: DockPanel[], activeId: string | null, size = DEFAULT_SIZE): DockSlot {
	return { id: nextSlotId(), panels, activeId, size };
}
function newStack(slots: DockSlot[], size = DEFAULT_SIZE): DockStack {
	return { id: nextStackId(), slots, size };
}

/** Every card, flattened — column structure is irrelevant to "where is panel X?". */
function allSlots(stacks: DockStack[]): DockSlot[] {
	return stacks.flatMap((st) => st.slots);
}
function findSlotOf(stacks: DockStack[], panelId: string): DockSlot | undefined {
	return allSlots(stacks).find((s) => s.panels.some((p) => p.id === panelId));
}
function findStackOfSlot(stacks: DockStack[], slotId: string): DockStack | undefined {
	return stacks.find((st) => st.slots.some((sl) => sl.id === slotId));
}

/**
 * Maximize is a VIEW MODE, not a layout fact — so it yields the moment it would
 * hide the card the user just acted on. Without this, a panel auto-opening into
 * a different card (or a tab split out to a new one) lands behind the maximized
 * card and reads as "nothing happened" — the exact failure the tab strip's
 * scrollIntoView already exists to prevent, one level up.
 */
function keepMaximize(current: string | null, landedSlotId: string): string | null {
	return current && current !== landedSlotId ? null : current;
}

/**
 * Drop a maximize pointing at a card that no longer exists. The dock renders
 * ONLY the maximized card, so a dangling id filters the grid down to nothing:
 * the dock would sit there empty while still holding panels.
 */
function pruneMaximize(stacks: DockStack[], current: string | null): string | null {
	return current && allSlots(stacks).some((sl) => sl.id === current) ? current : null;
}

/** Drop any column left with no cards, so an emptied column doesn't linger as a
 *  zero-width gap that still eats a flex share. */
function compact(stacks: DockStack[]): DockStack[] {
	return stacks.filter((st) => st.slots.length > 0);
}

export const useDockStore = create<DockState>((set) => ({
	open: false,
	stacks: [],
	width: initialWidth(),
	maximizedSlotId: null,

	openPanel: (panel, slotId) =>
		set((s) => {
			// Already open anywhere -> focus it, never clone.
			const existing = findSlotOf(s.stacks, panel.id);
			if (existing) {
				return {
					open: true,
					maximizedSlotId: keepMaximize(s.maximizedSlotId, existing.id),
					stacks: s.stacks.map((st) => ({
						...st,
						slots: st.slots.map((sl) =>
							sl.id === existing.id ? { ...sl, activeId: panel.id } : sl,
						),
					})),
				};
			}

			const stacks = s.stacks.map((st) => ({ ...st, slots: st.slots.map((sl) => ({ ...sl, panels: [...sl.panels] })) }));
			let target = slotId
				? allSlots(stacks).find((sl) => sl.id === slotId)
				: stacks[0]?.slots[0];
			if (!target) {
				// Empty dock (or an unknown slotId): one column, one card.
				target = newSlot([], null);
				if (stacks[0]) stacks[0].slots.push(target);
				else stacks.push(newStack([target]));
			}
			target.panels.push(panel);
			// Evict from the left (oldest), never the panel being opened.
			while (target.panels.length > MAX_TABS_PER_SLOT) {
				const victim = target.panels.findIndex((p) => p.id !== panel.id);
				if (victim < 0) break;
				target.panels.splice(victim, 1);
			}
			target.activeId = panel.id;
			return { open: true, stacks, maximizedSlotId: keepMaximize(s.maximizedSlotId, target.id) };
		}),

	closePanel: (id) =>
		set((s) => {
			const owner = findSlotOf(s.stacks, id);
			if (!owner) return {};
			const idx = owner.panels.findIndex((p) => p.id === id);
			const panels = owner.panels.filter((p) => p.id !== id);

			if (panels.length === 0) {
				// Card empty -> drop the card. Column empty -> drop the column. No
				// columns -> hide the dock. Each step only fires because the one
				// below it emptied; skipping the middle is how a zero-card column
				// survives as a dead flex share.
				const stacks = compact(
					s.stacks.map((st) => ({ ...st, slots: st.slots.filter((sl) => sl.id !== owner.id) })),
				);
				if (stacks.length === 0) return { stacks: [], open: false, maximizedSlotId: null };
				return { stacks, maximizedSlotId: pruneMaximize(stacks, s.maximizedSlotId) };
			}
			const activeId =
				owner.activeId === id
					? // Focus whatever slid into the closed tab's index, else the new last.
						panels[Math.min(idx, panels.length - 1)]!.id
					: owner.activeId;
			return {
				stacks: s.stacks.map((st) => ({
					...st,
					slots: st.slots.map((sl) => (sl.id === owner.id ? { ...sl, panels, activeId } : sl)),
				})),
			};
		}),

	closeSlot: (slotId) =>
		set((s) => {
			if (!allSlots(s.stacks).some((sl) => sl.id === slotId)) return {};
			const stacks = compact(
				s.stacks.map((st) => ({ ...st, slots: st.slots.filter((sl) => sl.id !== slotId) })),
			);
			// Same contract as closing the last tab: nothing left -> hide the dock.
			if (stacks.length === 0) return { stacks: [], open: false, maximizedSlotId: null };
			return { stacks, maximizedSlotId: pruneMaximize(stacks, s.maximizedSlotId) };
		}),

	replacePanel: (id, next) =>
		set((s) => {
			const owner = findSlotOf(s.stacks, id);
			if (!owner) return {};
			// Replacing a panel with an id that is already open ELSEWHERE would put
			// the same panel in two tabs, which the add-or-focus contract exists to
			// prevent. A no-op id (id === next.id) is fine — that is a content update.
			if (next.id !== id && findSlotOf(s.stacks, next.id)) return {};
			return {
				stacks: s.stacks.map((st) => ({
					...st,
					slots: st.slots.map((sl) =>
						sl.id === owner.id
							? {
									...sl,
									panels: sl.panels.map((p) => (p.id === id ? next : p)),
									activeId: sl.activeId === id ? next.id : sl.activeId,
								}
							: sl,
					),
				})),
			};
		}),

	setActive: (panelId) =>
		set((s) => {
			const owner = findSlotOf(s.stacks, panelId);
			if (!owner) return {};
			return {
				stacks: s.stacks.map((st) => ({
					...st,
					slots: st.slots.map((sl) => (sl.id === owner.id ? { ...sl, activeId: panelId } : sl)),
				})),
			};
		}),

	splitPanelToNewSlot: (panelId) =>
		set((s) => {
			const owner = findSlotOf(s.stacks, panelId);
			if (!owner) return {};
			const panel = owner.panels.find((p) => p.id === panelId)!;
			// A lone panel is already its own card — splitting would just move the
			// card, which is a no-op the user would read as a bug.
			if (owner.panels.length === 1) return {};
			const host = findStackOfSlot(s.stacks, owner.id)!;
			const idx = owner.panels.findIndex((p) => p.id === panelId);
			const remaining = owner.panels.filter((p) => p.id !== panelId);
			const trimmed: DockSlot = {
				...owner,
				panels: remaining,
				activeId:
					owner.activeId === panelId
						? remaining[Math.min(idx, remaining.length - 1)]!.id
						: owner.activeId,
				// The pair splits the origin's share evenly, so a split does not shove
				// the column's other cards around.
				size: owner.size / 2,
			};
			const fresh = newSlot([panel], panelId, owner.size / 2);
			const at = host.slots.findIndex((sl) => sl.id === owner.id);
			const slots = [...host.slots];
			slots.splice(at, 1, trimmed, fresh); // new card sits directly below its origin
			return {
				stacks: s.stacks.map((st) => (st.id === host.id ? { ...st, slots } : st)),
				// Splitting asks to see TWO cards — a maximize would hide the new one.
				maximizedSlotId: keepMaximize(s.maximizedSlotId, fresh.id),
			};
		}),

	moveSlot: (slotId, toStackIndex, toIndex) =>
		set((s) => {
			const from = findStackOfSlot(s.stacks, slotId);
			if (!from) return {};
			const slot = from.slots.find((sl) => sl.id === slotId)!;
			const fromStackIdx = s.stacks.findIndex((st) => st.id === from.id);
			const fromIdx = from.slots.findIndex((sl) => sl.id === slotId);

			// Dropping past the last column means "make a new one" — that is how a
			// second column comes into existence at all. Pointless when the card is
			// already alone in its own column: it would just swap one lone column
			// for another, which reads as nothing happening.
			const makingNew = toStackIndex >= s.stacks.length;
			if (makingNew && from.slots.length === 1) return {};

			let stacks = s.stacks.map((st) => ({ ...st, slots: [...st.slots] }));
			stacks[fromStackIdx]!.slots.splice(fromIdx, 1);

			if (makingNew) {
				// The new column takes an equal share; it is a peer, not a sliver.
				stacks.push(newStack([slot]));
			} else {
				const dest = stacks[Math.max(0, Math.min(toStackIndex, stacks.length - 1))]!;
				// Clamp rather than refuse: a drag past the last card means "put it
				// last", which is the obvious intent, not an error.
				const to = Math.max(0, Math.min(toIndex, dest.slots.length));
				if (dest.id === from.id && to === fromIdx) return {}; // no-op
				dest.slots.splice(to, 0, slot);
			}
			// `size` travels ON the slot, so a card keeps its height through a move.
			stacks = compact(stacks);
			return { stacks };
		}),

	toggleMaximize: (slotId) =>
		set((s) => {
			if (!allSlots(s.stacks).some((sl) => sl.id === slotId)) return {};
			return { maximizedSlotId: s.maximizedSlotId === slotId ? null : slotId };
		}),

	resizeSlotPair: (aId, bId, aSize, bSize) =>
		set((s) => {
			if (!validPair(aSize, bSize)) return {};
			const slots = allSlots(s.stacks);
			if (!slots.some((sl) => sl.id === aId) || !slots.some((sl) => sl.id === bId)) return {};
			return {
				stacks: s.stacks.map((st) => ({
					...st,
					slots: st.slots.map((sl) =>
						sl.id === aId ? { ...sl, size: aSize } : sl.id === bId ? { ...sl, size: bSize } : sl,
					),
				})),
			};
		}),

	resizeStackPair: (aId, bId, aSize, bSize) =>
		set((s) => {
			if (!validPair(aSize, bSize)) return {};
			if (!s.stacks.some((st) => st.id === aId) || !s.stacks.some((st) => st.id === bId)) return {};
			return {
				stacks: s.stacks.map((st) =>
					st.id === aId ? { ...st, size: aSize } : st.id === bId ? { ...st, size: bSize } : st,
				),
			};
		}),

	openDock: () => set({ open: true }),
	close: () => set({ open: false }),

	setWidth: (px) => {
		const clamped = Math.max(MIN_WIDTH, px);
		try {
			localStorage.setItem(WIDTH_KEY, String(clamped));
		} catch {
			/* private mode / quota — width just won't persist */
		}
		set({ width: clamped });
	},
}));

/** A zero/negative/NaN weight collapses a card or column to nothing with no way
 *  to drag it back — the drag math is the caller's, so refuse rather than trust. */
function validPair(a: number, b: number): boolean {
	return a > 0 && b > 0 && Number.isFinite(a) && Number.isFinite(b);
}

/** The active panel of a given card. Takes only what it reads, so a caller with
 *  a bare {panels, activeId} need not fabricate a layout to ask the question. */
export function activePanel(slot: Pick<DockSlot, "panels" | "activeId">): DockPanel | null {
	return slot.panels.find((p) => p.id === slot.activeId) ?? null;
}

/** Every open panel, flattened — for "is this thing already open?" checks. */
export function allPanels(s: Pick<DockState, "stacks">): DockPanel[] {
	return s.stacks.flatMap((st) => st.slots.flatMap((sl) => sl.panels));
}

/** The columns the dock should draw: the whole grid, or just the maximized card
 *  alone in its column. `pruneMaximize` is what keeps this from ever returning
 *  an empty grid for a non-empty dock.
 *
 *  `scope` (the current module, e.g. "media") filters what THIS module shows:
 *  a panel with a scope renders only in its own module; scope-less panels
 *  (terminals) render everywhere. Filtering happens at render — the store
 *  keeps every panel, so switching back to a module restores its results. */
export function visibleStacks(
	s: Pick<DockState, "stacks" | "maximizedSlotId">,
	scope?: string,
): DockStack[] {
	let stacks = s.stacks;
	if (scope !== undefined) {
		stacks = stacks
			.map((st) => ({
				...st,
				slots: st.slots
					.map((sl) => {
						const panels = sl.panels.filter((p) => !p.scope || p.scope === scope);
						const activeId = panels.some((p) => p.id === sl.activeId)
							? sl.activeId
							: (panels[0]?.id ?? null);
						return { ...sl, panels, activeId };
					})
					.filter((sl) => sl.panels.length > 0),
			}))
			.filter((st) => st.slots.length > 0);
	}
	if (!s.maximizedSlotId) return stacks;
	const owner = stacks.find((st) => st.slots.some((sl) => sl.id === s.maximizedSlotId));
	if (!owner) return stacks;
	return [{ ...owner, slots: owner.slots.filter((sl) => sl.id === s.maximizedSlotId) }];
}

/**
 * Cross-module result surface (Rule 29 — NO DEAD ENDS): push any
 * document-shaped result into the dock as an artifact panel — rendered
 * preview, Save/Copy/Download toolbar — and summon the dock. Callable from
 * ANY module (the dock mounts at the Shell, so it is available everywhere).
 * Use a stable per-document id so re-viewing focuses the existing tab
 * instead of cloning it.
 */
export function showResultInDock(result: {
	id: string;
	title: string;
	content: string;
	language?: string;
	/** The pushing MODULE ("media", "wiki", …) — the panel renders only there
	 *  (modules show their own results). Omit for a deliberately global result. */
	scope?: string;
}): void {
	const panel: DockPanel = {
		id: result.id,
		kind: "artifact",
		title: result.title,
		data: { language: result.language ?? "markdown", content: result.content },
		scope: result.scope,
	};
	const store = useDockStore.getState();
	// A stable id per document is what stops a re-push from cloning a tab —
	// but `openPanel` only FOCUSES an id it already holds, so a regenerated
	// result (same deck, new slides) would leave the user looking at the old
	// one with no sign anything had changed. Update in place when the tab
	// exists: same id + same content is a no-op, same id + new content shows
	// the new content, and neither can duplicate (2026-09-10).
	if (allPanels(store).some((p) => p.id === panel.id)) {
		store.replacePanel(panel.id, panel);
		store.setActive(panel.id);
		store.openDock();
		return;
	}
	store.openPanel(panel);
}
